import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WorkspaceQuarantineSettingsInput } from "@unbox/api";
import { client } from "@/api/client";
import { handleError } from "@/api/error";

// Workspace-level safety template shapes, sourced from the API.
export type { WorkspacePolicyTemplateStep, WorkspaceSafetyPolicyTemplateItem } from "@unbox/api";

const workspacePolicyTemplatesKey = (workspaceId: string) => ["workspace-safety-policy-templates", workspaceId];
const workspaceQuarantineKey = (workspaceId: string) => ["workspace-quarantine-settings", workspaceId];

// The workspace's policy templates with their steps. The API seeds the eight
// predefined policies on first read, so this never comes back empty.
export const useWorkspaceSafetyPolicyTemplates = (workspaceId: string) =>
  useQuery({
    queryKey: workspacePolicyTemplatesKey(workspaceId),
    queryFn: async () => {
      const res = await client.workspace[":workspaceId"]["safety-policy-templates"].$get({ param: { workspaceId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!workspaceId,
  });

/** Patch the editable fields of a policy template's steps (delay, message, opt-in). */
export const useUpdateWorkspacePolicyTemplateSteps = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      policyId,
      steps,
    }: {
      policyId: string;
      steps: {
        step: number;
        delayDays?: number;
        sendAtHour?: number | null;
        sendAtTimezone?: string | null;
        customMessage?: string | null;
        enabled?: boolean;
      }[];
    }) => {
      const res = await client.workspace[":workspaceId"]["safety-policy-templates"][":id"].steps.$put({
        param: { workspaceId, id: policyId },
        json: { steps },
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspacePolicyTemplatesKey(workspaceId) });
    },
  });
};

export const useWorkspaceQuarantineSettings = (workspaceId: string) =>
  useQuery({
    queryKey: workspaceQuarantineKey(workspaceId),
    queryFn: async () => {
      const res = await client.workspace[":workspaceId"]["quarantine-settings"].$get({ param: { workspaceId } });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    enabled: !!workspaceId,
  });

export const useUpdateWorkspaceQuarantineSettings = (workspaceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: WorkspaceQuarantineSettingsInput) => {
      const res = await client.workspace[":workspaceId"]["quarantine-settings"].$put({
        param: { workspaceId },
        json: input,
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceQuarantineKey(workspaceId) });
    },
  });
};
